/* eslint-disable max-lines */
import { useState } from 'react'
import { Box, Collapse, Stack, Typography } from '@mui/material'
import {
  ChevronDown20Regular,
  ChevronRight20Regular,
  DocumentText20Regular,
} from '@/components/Icon'
import Pressable from '@/theme/Pressable'
import { useDocumentPreviewOptional } from '@/features/documentPreview/useDocumentPreview'
import {
  citationDocTitle,
  citationLocationLabel,
  citationLocationStatus,
} from './citationLabel'
import { openCitationPreview } from './openCitationPreview'

export default function SourcesPanel({ citations = [] }) {
  const [open, setOpen] = useState(false)
  const preview = useDocumentPreviewOptional()

  if (!citations.length) return null

  return (
    <Box sx={{ mt: 1.5 }}>
      <Pressable
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: 12,
          fontWeight: 650,
          color: 'var(--app-text-secondary)',
          '&:hover': { color: 'var(--app-text-primary)' },
        }}
      >
        {open ? (
          <ChevronDown20Regular fontSize={14} />
        ) : (
          <ChevronRight20Regular fontSize={14} />
        )}
        Sources ({citations.length})
      </Pressable>

      <Collapse in={open}>
        <Stack sx={{ gap: 1, mt: 1 }}>
          {citations.map((citation, index) => {
            const locationStatus = citationLocationStatus(citation)
            return (
              <Pressable
                key={`${citation.doc_id}-${index}`}
                onClick={() =>
                  openCitationPreview(preview, citation, citations)
                }
                aria-label={`Open ${citationDocTitle(citation)} at ${citationLocationLabel(citation)}`}
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  p: 1.25,
                  borderRadius: 'var(--app-radius-sm)',
                  border: '1px solid var(--app-border-light)',
                  bgcolor: 'var(--app-bg-panel)',
                  '&:hover': { bgcolor: 'var(--app-bg-hover)' },
                }}
              >
                <Box sx={{ mt: '1px', color: 'var(--app-text-tertiary)' }}>
                  <DocumentText20Regular fontSize={16} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Stack
                    direction="row"
                    sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 650,
                        color: 'var(--app-text-primary)',
                      }}
                    >
                      {typeof citation.marker === 'number'
                        ? `[${citation.marker}] `
                        : ''}
                      {citationDocTitle(citation)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: 'var(--app-text-secondary)',
                      }}
                    >
                      {citationLocationLabel(citation)}
                    </Typography>
                    {locationStatus ? (
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: 'var(--app-warning, #d83b01)',
                          fontWeight: 650,
                        }}
                      >
                        {locationStatus}
                      </Typography>
                    ) : null}
                  </Stack>
                  {citation.snippet ? (
                    <Typography
                      sx={{
                        mt: 0.5,
                        fontSize: 12,
                        color: 'var(--app-text-secondary)',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {citation.snippet}
                    </Typography>
                  ) : null}
                </Box>
              </Pressable>
            )
          })}
        </Stack>
      </Collapse>
    </Box>
  )
}
